<template>
  <v-container fluid>
    <v-row>
      <v-col class="text-h3 gray--text"> Teste de lentes de contato </v-col>

      <v-col cols="auto" class="mt-1">
        <SaveStatus
          not-saved-message="O teste ainda não foi salvo."
          :saving="saveManager.isSaving"
          :pending="saveManager.pendingSave"
          :saved-at="updatedAt"
          :loading="isLoading"
          :error="hasErrors || saveManager.isError"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="light-blue"
          dark
          :loading="saveManager.isSaving || isLoading"
          @click="saveManager.triggerManualSave()"
        >
          Salvar
        </v-btn>
      </v-col>

      <v-col cols="auto">
        <v-btn
          color="gray lighten-1"
          dark
          :to="`/prontuario/${attendance.id}/lentes-contato`"
        >
          voltar
        </v-btn>
      </v-col>
    </v-row>

    <DynamicFormCore
      dynamicFormType="contact_lens_test"
      :answerableId="attendance.id"
      answerableType="Attendance"
      :disabled="true"
    />

    <ContactLensTestFormSkeletonLoader v-if="isLoading" />
    <v-row v-else>
      <v-col>
        <LensInformationSection
          :isAdapted.sync="isAdapted"
          :eye.sync="eye"
          :contactLensTestGroup.sync="contactLensTestGroup"
          :material.sync="material"
          :contactLensTestLens.sync="contactLensTestLens"
          :diopter.sync="diopter"
          :radius.sync="radius"
          :contactLensSpherical.sync="contactLensSpherical"
          :diameter.sync="diameter"
          :contactLensObservation.sync="contactLensObservation"
          :facilitators="facilitators"
          :materials="materials"
          :isFormDisabled="attendanceEsigned"
          :errors="errors"
          :contactLensTestGroups="contactLensTestGroups"
          :hasContactLensTestLens="hasContactLensTestLens"
          @change-spherical="changeSpherical"
          @dispatch-auto-save="dispatchAutoSave"
        />
        <LensAdaptationSection
          :contactLensSpherical.sync="contactLensSpherical"
          :contactLensCylindrical.sync="contactLensCylindrical"
          :contactLensAxis.sync="contactLensAxis"
          :contactLensVisualAcuity.sync="contactLensVisualAcuity"
          :refractedSpherical.sync="refractedSpherical"
          :refractedCylindrical.sync="refractedCylindrical"
          :refractedAxis.sync="refractedAxis"
          :refractedVisualAcuity.sync="refractedVisualAcuity"
          :finalSpherical.sync="finalSpherical"
          :finalCylindrical.sync="finalCylindrical"
          :finalAxis.sync="finalAxis"
          :finalVisualAcuity.sync="finalVisualAcuity"
          :notes.sync="notes"
          :facilitators="facilitators"
          :isFormDisabled="attendanceEsigned"
          :hasContactLensTestLens="hasContactLensTestLens"
          @change-spherical="changeSpherical"
          @dispatch-auto-save="dispatchAutoSave"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import SaveStatus from "@/components/ui/form/save_status/index.vue";
import DynamicFormCore from "@/components/dynamic_form/core.vue";
import ContactLensTestFormSkeletonLoader from "@/components/contact_lens_test/form/skeleton_loader.vue";
import LensInformationSection from "@/views/contact_lens_tests/form/lens_information_section.vue";
import LensAdaptationSection from "@/views/contact_lens_tests/form/lens_adaptation_section.vue";

import { AutoSaveManager } from "@/services/AutoSaveManager/index.js";
import { sumLensValues } from "@/views/contact_lens_tests/form/sumLensValues.js";

const EMPTY_FIELD_MESSAGE = "Esse campo deve ser preenchido";

export default {
  name: "ContactLensTestForm",
  components: {
    SaveStatus,
    DynamicFormCore,
    ContactLensTestFormSkeletonLoader,
    LensInformationSection,
    LensAdaptationSection,
  },
  props: {
    attendance: Object,
    attendanceEsigned: Boolean,
    contactLensTestId: String,
  },
  data() {
    return {
      errors: {},
      materials: [],
      contactLensTestGroup: null,
      contactLensTestLens: null,
      eye: "",
      material: null,
      contactLensSpherical: "Em branco",
      diopter: "",
      radius: "",
      diameter: "",
      contactLensObservation: "",
      contactLensCylindrical: "",
      contactLensAxis: "",
      contactLensVisualAcuity: "",
      refractedSpherical: "Em branco",
      refractedCylindrical: "",
      refractedAxis: "",
      refractedVisualAcuity: "",
      finalSpherical: "Em branco",
      finalCylindrical: "",
      finalAxis: "",
      finalVisualAcuity: "",
      notes: "",
      isAdapted: false,
      saveManager: null,
      updatedAt: null,
    };
  },
  created() {
    this.saveManager = new AutoSaveManager({
      formName: "Teste de Lentes de Contato",
      beforeSave: () => this.beforeSave(),
      onSave: () => this.onSave(),
      autoSaveTimeout: 4000,
    });
  },
  apollo: {
    contactLensTest: {
      query: require("@/graphql/contact_lens_test/contact_lens_test.gql"),
      fetchPolicy: "no-cache",
      variables() {
        return {
          contactLensTestId: this.contactLensTestId,
        };
      },
      result({ data }) {
        this.updatedAt = data.contactLensTest.updatedAt;
        this.loadContactLensTest();
      },
    },
    contactLensTestGroups: {
      query: require("@/views/contact_lens_tests/form/contact_lens_test_groups.gql"),
    },
    facilitators: {
      query: require("@/graphql/contact_lens_test/facilitators.gql"),
      update: (data) => data.contactLensTestFacilitators,
    },
    materials: {
      query: require("@/graphql/contact_lens_test/materials.gql"),
      update: (data) => data.contactLensTestMaterials,
    },
  },
  computed: {
    isLoading() {
      if (!this.contactLensTest) return true;
      if (this.$apollo.queries.contactLensTestGroups.loading) return true;
      if (this.$apollo.queries.facilitators.loading) return true;
      if (this.$apollo.queries.materials.loading) return true;

      return false;
    },
    hasContactLensTestLens() {
      return Boolean(this.contactLensTestLens?.id);
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
  },
  methods: {
    loadContactLensTest() {
      this.loadContactLensTestMaterial();
      this.loadContactLensTestLens();

      this.eye = this.contactLensTest.eye;
      this.diopter = this.contactLensTest.diopter;
      this.radius = this.contactLensTest.radius;
      this.diameter = this.contactLensTest.diameter;
      this.contactLensObservation = this.contactLensTest.contactLensObservation;
      this.contactLensSpherical = this.contactLensTest.contactLensSpherical;
      this.contactLensCylindrical = this.contactLensTest.contactLensCylindrical;
      this.contactLensAxis = this.contactLensTest.contactLensAxis;
      this.contactLensVisualAcuity = this.contactLensTest.contactLensVisualAcuity;
      this.refractedSpherical = this.contactLensTest.refractedSpherical;
      this.refractedCylindrical = this.contactLensTest.refractedCylindrical;
      this.refractedAxis = this.contactLensTest.refractedAxis;
      this.refractedVisualAcuity = this.contactLensTest.refractedVisualAcuity;
      this.finalSpherical = this.contactLensTest.finalSpherical;
      this.finalCylindrical = this.contactLensTest.finalCylindrical;
      this.finalAxis = this.contactLensTest.finalAxis;
      this.finalVisualAcuity = this.contactLensTest.finalVisualAcuity;
      this.notes = this.contactLensTest.notes;
      this.isAdapted = this.contactLensTest.adapted;

      if (!this.contactLensSpherical) {
        this.contactLensSpherical = "Em branco";
      }

      if (!this.refractedSpherical) {
        this.refractedSpherical = "Em branco";
      }

      if (!this.finalSpherical) {
        this.finalSpherical = "Em branco";
      }
    },
    loadContactLensTestMaterial() {
      if (!this.contactLensTest?.material) return;

      this.material = this.contactLensTest.material;
    },
    loadContactLensTestLens() {
      if (!this.contactLensTest?.lens) return;

      this.contactLensTestLens = this.contactLensTest.lens;
      this.contactLensTestGroup = this.contactLensTestLens.group;
    },

    async changeSpherical() {
      if (this.attendanceEsigned) return;

      await this.$nextTick();

      this.finalSpherical = sumLensValues(
        this.contactLensSpherical,
        this.refractedSpherical
      );

      this.dispatchAutoSave();
    },

    dispatchAutoSave() {
      this.saveManager.dispatchAutoSave();
    },

    validate() {
      this.errors = {};

      if (!this.contactLensTestGroup?.id) {
        this.errors.contactLensTestGroup = EMPTY_FIELD_MESSAGE;
      }

      if (!this.contactLensTestLens?.id) {
        this.errors.contactLensTestLens = EMPTY_FIELD_MESSAGE;
      }
    },

    beforeSave() {
      if (this.attendanceEsigned) return false;

      this.validate();

      return !this.hasErrors;
    },
    async onSave() {
      const { data } = await this.apolloMutate({
        mutation: require("@/graphql/contact_lens_test/save_contact_lens_test.gql"),
        variables: {
          attendanceId: this.attendance.id,
          contactLensTestId: this.contactLensTest.id,
          contactLensTestMaterialId: this.material.id,
          contactLensTestLensId: this.contactLensTestLens.id,
          eye: this.eye,
          diopter: this.diopter,
          radius: this.radius,
          diameter: this.diameter,
          contactLensObservation: this.contactLensObservation,
          contactLensSpherical: this.contactLensSpherical,
          contactLensCylindrical: this.contactLensCylindrical,
          contactLensAxis: this.contactLensAxis,
          contactLensVisualAcuity: this.contactLensVisualAcuity,
          refractedSpherical: this.refractedSpherical,
          refractedCylindrical: this.refractedCylindrical,
          refractedAxis: this.refractedAxis,
          refractedVisualAcuity: this.refractedVisualAcuity,
          finalSpherical: this.finalSpherical,
          finalCylindrical: this.finalCylindrical,
          finalAxis: this.finalAxis,
          finalVisualAcuity: this.finalVisualAcuity,
          notes: this.notes,
          adapted: this.isAdapted,
        },
      });

      this.updatedAt = data.saveContactLensTest.updatedAt;
    },
  },
};
</script>

<style lang="scss" scoped></style>
